import type { CurationCandidate, RefineClip } from '../api/types';

export type ClipBounds = { start: number; end: number };

export function formatClipSec(sec: number): string {
  const s = Math.max(0, sec);
  const m = Math.floor(s / 60);
  const r = (s % 60).toFixed(1);
  return m > 0 ? `${m}:${r.padStart(4, '0')}` : `${Number(r).toFixed(1)}s`;
}

export function formatTotalClock(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

/** Immutable shrink-only rails from detect (safe end when present). */
export function trimRails(c: CurationCandidate): ClipBounds {
  const lo =
    typeof c.detected_start_sec === 'number' ? c.detected_start_sec : c.start_sec;
  const hi =
    typeof c.detected_safe_end_sec === 'number'
      ? c.detected_safe_end_sec
      : c.end_sec;
  return { start: lo, end: Math.max(lo, hi) };
}

export function initialBounds(c: CurationCandidate): ClipBounds {
  const rails = trimRails(c);
  const start =
    typeof c.current_start_sec === 'number' ? c.current_start_sec : rails.start;
  const end =
    typeof c.current_end_sec === 'number' ? c.current_end_sec : rails.end;
  return {
    start: Math.max(rails.start, Math.min(start, rails.end)),
    end: Math.max(rails.start, Math.min(end, rails.end)),
  };
}

export function clampBounds(
  rails: ClipBounds,
  start: number,
  end: number,
  minSpan = 1
): ClipBounds {
  let s = Math.max(rails.start, Math.min(start, rails.end));
  let e = Math.max(rails.start, Math.min(end, rails.end));
  if (e < s + minSpan) {
    e = Math.min(rails.end, s + minSpan);
    if (e - s < minSpan) s = Math.max(rails.start, e - minSpan);
  }
  return { start: s, end: e };
}

export function clipsFingerprint(
  orderedIds: string[],
  bounds: Record<string, ClipBounds>
): string {
  return orderedIds
    .map((id) => {
      const b = bounds[id];
      if (!b) return id;
      return `${id}:${b.start.toFixed(3)}:${b.end.toFixed(3)}`;
    })
    .join('|');
}

export function buildRefineClips(
  orderedIds: string[],
  bounds: Record<string, ClipBounds>,
  byId: Map<string, CurationCandidate>
): RefineClip[] {
  return orderedIds
    .map((id) => {
      const c = byId.get(id);
      if (!c) return null;
      const b = bounds[id] || initialBounds(c);
      return {
        candidate_id: id,
        start_sec: b.start,
        end_sec: b.end,
      };
    })
    .filter((row): row is RefineClip => row != null);
}

/** Seed ordered IDs: draft/in-reel first (API order), else chronological. */
export function seedOrderedIds(candidates: CurationCandidate[]): string[] {
  const selected = candidates.filter(
    (c) => c.selected_by_default || c.in_current_reel || c.in_reel
  );
  if (selected.length === 0) return [];
  // Prefer API order (draft order when refining a delivered reel).
  const hasDraftFlags = candidates.some((c) => c.in_current_reel || c.in_reel);
  if (hasDraftFlags) {
    return selected.map((c) => c.candidate_id);
  }
  return [...selected]
    .sort((a, b) => a.start_sec - b.start_sec)
    .map((c) => c.candidate_id);
}

/** Insert id into ordered list keeping chronological position among selected. */
export function insertChronologically(
  orderedIds: string[],
  id: string,
  byId: Map<string, CurationCandidate>
): string[] {
  if (orderedIds.includes(id)) return orderedIds;
  const cand = byId.get(id);
  if (!cand) return [...orderedIds, id];
  const next = [...orderedIds];
  const start = cand.start_sec;
  let insertAt = next.length;
  for (let i = 0; i < next.length; i += 1) {
    const other = byId.get(next[i]);
    if (other && other.start_sec > start) {
      insertAt = i;
      break;
    }
  }
  next.splice(insertAt, 0, id);
  return next;
}

export const MAX_MUSIC_FADE_S = 5;

const CAMERA_CLIP_KINDS = new Set(['live', 'replay', 'cutscene', 'menu']);

type PlayTypeChip = { id: string; label: string };

const PLAY_TYPE_CHIPS: Array<PlayTypeChip & { aliases: string[]; pattern: RegExp }> =
  [
    {
      id: 'td',
      label: 'TD',
      aliases: ['td', 'touchdown', 'pick-six', 'picksix', 'pick six'],
      pattern: /\b(td|touchdown|pick[\s-]?six)\b/i,
    },
    {
      id: 'int',
      label: 'INT',
      aliases: ['int', 'interception'],
      pattern: /\b(int|interception)\b/i,
    },
    {
      id: 'sack',
      label: 'Sack',
      aliases: ['sack'],
      pattern: /\b(sack)\b/i,
    },
    {
      id: 'fg',
      label: 'FG',
      aliases: ['fg', 'field goal', 'field-goal'],
      pattern: /\b(field[\s-]?goal|\bfg\b)\b/i,
    },
    {
      id: 'fumble',
      label: 'Fumble',
      aliases: ['fumble'],
      pattern: /\b(fumble)\b/i,
    },
    {
      id: 'safety',
      label: 'Safety',
      aliases: ['safety'],
      pattern: /\b(safety)\b/i,
    },
  ];

function normalizePlayTypeKey(raw: string): string {
  return raw.trim().toLowerCase().replace(/[_]+/g, ' ').replace(/\s+/g, ' ');
}

function chipFromExplicit(raw: string): PlayTypeChip | null {
  const key = normalizePlayTypeKey(raw);
  if (!key) return null;
  for (const chip of PLAY_TYPE_CHIPS) {
    if (chip.aliases.includes(key) || chip.id === key) return { id: chip.id, label: chip.label };
  }
  return null;
}

/** Read-only play-type chip from existing candidate fields. Null if unknown. */
export function playTypeChip(c: CurationCandidate): PlayTypeChip | null {
  const explicit = chipFromExplicit(c.play_type || '');
  if (explicit) return explicit;
  const kind = (c.clip_kind || '').trim();
  if (kind && !CAMERA_CLIP_KINDS.has(kind.toLowerCase())) {
    const fromKind = chipFromExplicit(kind);
    if (fromKind) return fromKind;
  }
  const text = `${c.label || ''} ${c.why || ''}`;
  for (const chip of PLAY_TYPE_CHIPS) {
    if (chip.pattern.test(text)) return { id: chip.id, label: chip.label };
  }
  return null;
}

/**
 * 0–1 heat from spectacle_score/score. Null when the field is missing —
 * callers render a neutral bar and must not invent a value.
 */
export function excitementHeat(c: CurationCandidate): number | null {
  const raw = c.spectacle_score ?? c.score;
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
  return Math.min(1, Math.max(0, raw / 2));
}

export function clampMusicFade(
  nextSec: number,
  otherFadeSec: number,
  totalDurationSec: number
): number {
  const other = Math.max(0, otherFadeSec);
  const room = Number.isFinite(totalDurationSec)
    ? Math.max(0, totalDurationSec - other)
    : MAX_MUSIC_FADE_S;
  const cap = Math.min(MAX_MUSIC_FADE_S, room);
  if (!Number.isFinite(nextSec)) return 0;
  return Math.max(0, Math.min(cap, nextSec));
}

export type PlaylistClockSegment = { startSec: number; endSec: number };

export function playlistDuration(segments: PlaylistClockSegment[]): number {
  return segments.reduce(
    (sum, segment) => sum + Math.max(0, segment.endSec - segment.startSec),
    0
  );
}

export function playlistTimeFromSource(
  segments: PlaylistClockSegment[],
  index: number,
  sourceTime: number
): number {
  let elapsed = 0;
  for (let i = 0; i < index; i += 1) {
    const segment = segments[i];
    if (!segment) break;
    elapsed += Math.max(0, segment.endSec - segment.startSec);
  }
  const current = segments[index];
  if (!current) return elapsed;
  const span = Math.max(0, current.endSec - current.startSec);
  const offset = Math.max(0, Math.min(span, sourceTime - current.startSec));
  return elapsed + offset;
}

export function playlistSeekTarget(
  segments: PlaylistClockSegment[],
  reelTime: number
): { index: number; sourceTime: number } | null {
  if (!segments.length) return null;
  let remaining = Math.max(0, reelTime);
  for (let i = 0; i < segments.length; i += 1) {
    const segment = segments[i];
    const span = Math.max(0, segment.endSec - segment.startSec);
    if (remaining <= span || i === segments.length - 1) {
      return {
        index: i,
        sourceTime: segment.startSec + Math.min(span, remaining),
      };
    }
    remaining -= span;
  }
  return null;
}

export function arrangementOffsetSec(
  orderedIds: string[],
  bounds: Record<string, ClipBounds>,
  clipId: string
): number {
  let offset = 0;
  for (const id of orderedIds) {
    if (id === clipId) return offset;
    const b = bounds[id];
    offset += b ? Math.max(0, b.end - b.start) : 0;
  }
  return offset;
}

export function reorderIds(
  orderedIds: string[],
  fromIndex: number,
  toIndex: number
): string[] {
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= orderedIds.length ||
    toIndex >= orderedIds.length ||
    fromIndex === toIndex
  ) {
    return orderedIds;
  }
  const next = [...orderedIds];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
}
